/**
 * `useActor()` (13-08): who is acting, for every verb, audit row and lens.
 *
 *   - UPN, display name and roles come from `useAuth` (MSAL account + App Role claims).
 *   - The site user id (`lookupId`, D-13-00-4) is resolved by `actionService.resolveActor`
 *     ONCE per user per page load here (one shared promise), and once per session through its
 *     sessionStorage cache.
 *   - Returns `{ status: 'loading' }` until that settles, then `{ status: 'ready', actor }`.
 *   - It never blocks the page on a failed lookup: `resolveActor` never throws, and a site that
 *     cannot be reached yields a ready Actor with `lookupId` undefined. The audit row's Author
 *     plus the UPN in its Note still identify the person (D-13-00-4).
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { resolveActor } from '../services/actionService';
import type { ActionIO } from '../services/actionService';
import type { Actor } from '../types/actionRegister';
import type { AppRole } from '../types/auth';
import { useActionRuntime } from './useActionRuntime';

export type ActorState = { status: 'loading'; actor: null } | { status: 'ready'; actor: Actor };

const LOADING: ActorState = { status: 'loading', actor: null };

/** One lookup per site + user per page load, shared by every component that asks. */
const inflight = new Map<string, Promise<string | undefined>>();

function actorKey(siteId: string, upn: string): string {
  return `${siteId}|${upn.trim().toLowerCase()}`;
}

function lookupOnce(io: ActionIO, upn: string): Promise<string | undefined> {
  const key = actorKey(io.siteId, upn);
  let p = inflight.get(key);
  if (!p) {
    p = resolveActor(io, upn).catch(() => undefined);
    inflight.set(key, p);
  }
  return p;
}

export function useActor(): ActorState {
  const { user } = useAuth();
  const { io, siteError } = useActionRuntime();

  const upn = user?.email ?? '';
  const displayName = user?.name ?? '';
  // A string key so a re-render with an equal roles array does not change the Actor's identity.
  const rolesKey = (user?.roles ?? []).join(',');
  const key = io && upn ? actorKey(io.siteId, upn) : '';

  const [resolved, setResolved] = useState<{ key: string; lookupId: string | undefined } | null>(null);

  useEffect(() => {
    if (!io || !upn) return;
    let cancelled = false;
    const k = actorKey(io.siteId, upn);
    lookupOnce(io, upn).then((lookupId) => {
      if (!cancelled) setResolved({ key: k, lookupId });
    });
    return () => {
      cancelled = true;
    };
  }, [io, upn]);

  return useMemo<ActorState>(() => {
    if (!upn) return LOADING;
    const roles = (rolesKey ? rolesKey.split(',') : []) as AppRole[];
    const base: Actor = { upn, displayName, roles };
    if (key && resolved?.key === key) {
      return { status: 'ready', actor: resolved.lookupId ? { ...base, lookupId: resolved.lookupId } : base };
    }
    // The site is unreachable: say who is acting anyway, without a lookup id.
    if (siteError) return { status: 'ready', actor: base };
    return LOADING;
  }, [upn, displayName, rolesKey, key, resolved, siteError]);
}
